import { useCallback, useEffect, useState } from "react";
import { HeartPulse, Brain, Loader2, Sparkles, RefreshCw, Send, AlertTriangle, ThumbsUp, Lightbulb, Users, Layers, Angry, Frown, Meh, Smile, Laugh } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";

const RISK_TONE = { bajo: "bg-emerald-500/15 text-emerald-600", medio: "bg-gold/15 text-gold", alto: "bg-destructive/15 text-destructive", sin_datos: "bg-navy/10 text-navy/50" };
const SCORE_ICONS = [Angry, Frown, Meh, Smile, Laugh];

export default function WellbeingAI() {
  const [meta, setMeta] = useState(null);
  const [pending, setPending] = useState(null);
  const [dash, setDash] = useState(null);
  const [insight, setInsight] = useState(null);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [pulseScore, setPulseScore] = useState(0);
  const [pulseComment, setPulseComment] = useState("");

  const load = useCallback(async () => {
    try {
      const m = await api.get("/wellbeing/meta"); setMeta(m.data);
      const p = await api.get("/wellbeing/pulse/pending"); setPending(p.data.pulse);
      if (m.data.is_leader) {
        const d = await api.get("/wellbeing/dashboard"); setDash(d.data);
        setInsight(d.data.capa_ia);
      }
    } catch { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const respondPulse = async () => {
    if (!pulseScore) return toast.error("Selecciona una calificación");
    setBusy(true);
    try { await api.post("/wellbeing/pulse/respond", { pulse_id: pending.id, score: pulseScore, comment: pulseComment }); toast.success("¡Gracias por tu respuesta!"); setPending(null); setPulseScore(0); setPulseComment(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };

  const runCampaign = async () => {
    try { const r = await api.post("/wellbeing/campaign/run"); toast.success(`${r.data.created} pulsos enviados hoy`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const generateAI = async () => {
    setAiBusy(true);
    try { const r = await api.post("/wellbeing/insights/generate"); setInsight(r.data); toast.success("Análisis de IA actualizado"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setAiBusy(false); }
  };

  return (
    <OSLayout title="OMNIA Bienestar con IA">
      <div className="space-y-6" data-testid="wellbeing-page">
        {/* Mi pulso de hoy */}
        {pending && (
          <div className="bg-gradient-violet text-white rounded-3xl p-6" data-testid="wellbeing-pulse">
            <div className="flex items-center gap-2 text-white/70 text-sm"><HeartPulse className="w-4 h-4" /> Tu pulso de hoy</div>
            <div className="font-editorial text-2xl mt-1">{pending.question}</div>
            <div className="flex gap-2 mt-4">
              {[1, 2, 3, 4, 5].map((s) => { const Ic = SCORE_ICONS[s - 1]; return (
                <button key={s} onClick={() => setPulseScore(s)} data-testid={`wellbeing-score-${s}`}
                  className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all ${pulseScore === s ? "bg-white text-violet scale-110" : "bg-white/15 text-white hover:bg-white/25"}`}><Ic className="w-6 h-6" /></button>
              ); })}
            </div>
            <input className="w-full rounded-xl bg-white/15 border border-white/20 text-white placeholder:text-white/50 px-3 py-2.5 text-sm mt-3" placeholder="Comentario (opcional)" value={pulseComment} onChange={(e) => setPulseComment(e.target.value)} data-testid="wellbeing-comment" />
            <button onClick={respondPulse} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-white text-violet px-5 py-2.5 text-sm font-medium mt-4 hover:opacity-90" data-testid="wellbeing-pulse-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Enviar</button>
          </div>
        )}
        {!pending && !meta?.is_leader && (
          <div className="bg-white border border-navy/10 rounded-2xl p-6 text-sm text-navy/50" data-testid="wellbeing-nopulse">No tienes pulsos pendientes hoy. ¡Gracias por cuidar tu bienestar!</div>
        )}

        {meta?.is_leader && dash && (
          <>
            {/* Arquitectura de 4 capas */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4" data-testid="wellbeing-dashboard">
              <LayerCard icon={HeartPulse} title="Capa 1 · Individual" tone="violet"
                rows={[["Mood Checks hoy", dash.capa_individual.mood_participacion], ["Ánimo promedio", dash.capa_individual.mood_avg ?? "—"], ["Participación", `${dash.capa_individual.participacion_pct}%`]]} />
              <LayerCard icon={Users} title="Capa 2 · Equipo" tone="sky"
                rows={[["Pulsos enviados", dash.capa_equipo.pulsos_enviados], ["Respondidos", dash.capa_equipo.pulsos_respondidos], ["Puntaje promedio", dash.capa_equipo.pulse_avg ?? "—"]]} />
              <LayerCard icon={Layers} title="Capa 3 · Organización" tone="gold"
                rows={[["Encuestas activas", dash.capa_organizacion.encuestas_activas], ["Respuestas totales", dash.capa_organizacion.respuestas_totales]]} />
              <LayerCard icon={Brain} title="Capa 4 · IA" tone="emerald"
                rows={[["Clima", insight?.clima_score != null ? `${insight.clima_score}/100` : "—"], ["Riesgo", insight?.nivel_riesgo || "—"]]} />
            </div>

            {/* Pulsos por categoría */}
            {dash.capa_equipo.por_categoria.length > 0 && (
              <div className="bg-white border border-navy/10 rounded-2xl p-4">
                <div className="text-sm font-medium mb-3">Pulsos de hoy por dimensión</div>
                <div className="space-y-2">{dash.capa_equipo.por_categoria.map((c) => (
                  <div key={c.category} className="flex items-center gap-3">
                    <span className="text-sm capitalize w-40">{c.category.replace("_", " ")}</span>
                    <div className="flex-1 h-2 rounded-full bg-navy/5 overflow-hidden"><div className="h-full bg-gradient-violet" style={{ width: `${(c.avg / 5) * 100}%` }} /></div>
                    <span className="text-xs text-navy/50 w-16 text-right">{c.avg}/5 ({c.n})</span>
                  </div>
                ))}</div>
              </div>
            )}

            {/* Motor de IA */}
            <div className="bg-white border border-navy/10 rounded-2xl p-5" data-testid="wellbeing-insight">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-violet" /><span className="font-editorial text-xl">Análisis de clima con IA</span></div>
                <div className="flex items-center gap-2">
                  <button onClick={runCampaign} className="btn btn-ghost text-xs gap-1" data-testid="wellbeing-run-campaign"><Send className="w-3.5 h-3.5" /> Enviar pulsos ({meta.sample_pct}%)</button>
                  <button onClick={generateAI} disabled={aiBusy} className="btn btn-primary text-xs gap-1" data-testid="wellbeing-generate-ai">{aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Analizar</button>
                </div>
              </div>
              {!insight ? <div className="text-sm text-navy/40">Pulsa "Analizar" para generar el análisis de clima con IA a partir de los pulsos y Mood Checks.</div>
                : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      {insight.clima_score != null && <div className="font-editorial text-4xl">{insight.clima_score}<span className="text-lg text-navy/40">/100</span></div>}
                      <span className={`text-xs px-2.5 py-1 rounded-full ${RISK_TONE[insight.nivel_riesgo] || "bg-navy/10"}`}>Riesgo {insight.nivel_riesgo}</span>
                    </div>
                    <p className="text-sm text-navy/70">{insight.resumen}</p>
                    <div className="grid gap-4 md:grid-cols-3">
                      <InsightList icon={AlertTriangle} title="Riesgos" items={insight.riesgos} tone="text-destructive" />
                      <InsightList icon={ThumbsUp} title="Fortalezas" items={insight.fortalezas} tone="text-emerald-600" />
                      <InsightList icon={Lightbulb} title="Recomendaciones" items={insight.recomendaciones} tone="text-violet" />
                    </div>
                  </div>
                )}
            </div>
          </>
        )}
      </div>
    </OSLayout>
  );
}

const TONE = { violet: "bg-violet/10 text-violet", sky: "bg-sky-500/10 text-sky-600", gold: "bg-gold/15 text-gold", emerald: "bg-emerald-500/10 text-emerald-600" };
function LayerCard({ icon: Ic, title, rows, tone }) {
  return (
    <div className="bg-white border border-navy/10 rounded-2xl p-4">
      <div className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs ${TONE[tone]}`}><Ic className="w-3.5 h-3.5" /> {title}</div>
      <div className="mt-3 space-y-1.5">{rows.map(([l, v]) => (
        <div key={l} className="flex items-center justify-between text-sm"><span className="text-navy/50">{l}</span><span className="font-medium">{v}</span></div>
      ))}</div>
    </div>
  );
}
function InsightList({ icon: Ic, title, items, tone }) {
  return (
    <div>
      <div className={`flex items-center gap-1.5 text-sm font-medium mb-2 ${tone}`}><Ic className="w-4 h-4" /> {title}</div>
      {(!items || items.length === 0) ? <div className="text-xs text-navy/30">—</div>
        : <ul className="space-y-1">{items.map((it, i) => (<li key={i} className="text-xs text-navy/70 flex gap-1.5"><span className="text-navy/30">•</span> {it}</li>))}</ul>}
    </div>
  );
}
